import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Search, Check, X, Download, Loader2, Eye, Trash2, RefreshCw, Filter, IdCard, Mail,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { resolveFileUrl } from '../../utils/fileUrl';
import PaginationBar from '../../components/PaginationBar';
import { formatDisplayDate } from '../../lib/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LIMIT = 20;

const STATUS_COLORS = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-rose-50 text-rose-700',
};

const StatCard = ({ label, value, color }) => (
  <div className={`rounded-xl p-4 border ${color}`}>
    <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-2xl font-bold mt-1">{value ?? '—'}</div>
  </div>
);

const VisionConventionManagement = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [wantsPresentFilter, setWantsPresentFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const token = localStorage.getItem('token');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (wantsPresentFilter) params.wants_present = wantsPresentFilter;
      if (search) params.search = search;
      params.skip = page * LIMIT;
      params.limit = LIMIT;
      const { data } = await axios.get(`${API_URL}/api/vc/admin/registrations`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, wantsPresentFilter, search, page, token]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/vc/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(data);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const doSearch = () => { setPage(0); setSearch(searchInput.trim()); };

  const updateStatus = async (reg, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/vc/admin/registrations/${reg.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Registration ${newStatus}`);
      fetchRows();
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const deleteReg = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API_URL}/api/vc/admin/registrations/${confirmDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Registration deleted');
      setConfirmDelete(null);
      fetchRows();
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get(`${API_URL}/api/vc/admin/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vision-convention-2026-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (e) {
      toast.error('Excel export failed');
    }
  };

  const downloadIdCard = async (reg) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/vc/admin/registrations/${reg.id}/id-card`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `VC-IDCard-${reg.registrationCode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('ID card downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ID card download failed');
    }
  };

  const resendIdCardEmail = async (reg) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/vc/admin/registrations/${reg.id}/resend-id-card`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`ID card email queued to ${res.data.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    }
  };

  // ============ Campaign Email — Vision Sangam 2026 invitation ============
  const [campaignPreview, setCampaignPreview] = useState(null); // { total, batchSize, breakdown }
  const [campaignSending, setCampaignSending] = useState(false);
  const [campaignProgress, setCampaignProgress] = useState({ current: 0, total: 0, sent: 0, failed: 0 });

  const openCampaign = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/vc/admin/campaign/recipients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaignPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recipient list');
    }
  };

  const closeCampaign = () => {
    if (campaignSending) return;
    setCampaignPreview(null);
    setCampaignProgress({ current: 0, total: 0, sent: 0, failed: 0 });
  };

  const sendCampaign = async () => {
    if (!campaignPreview) return;
    const { total, batchSize } = campaignPreview;
    if (!total) {
      toast.error('No recipients');
      return;
    }
    setCampaignSending(true);
    const batches = Math.ceil(total / batchSize);
    let sent = 0;
    let failed = 0;
    try {
      for (let i = 0; i < batches; i++) {
        const skip = i * batchSize;
        setCampaignProgress({ current: i + 1, total: batches, sent, failed });
        const res = await axios.post(
          `${API_URL}/api/vc/admin/campaign/send?skip=${skip}&limit=${batchSize}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        sent += res.data.sent || 0;
        failed += res.data.failed || 0;
        setCampaignProgress({ current: i + 1, total: batches, sent, failed });
      }
      toast.success(`Campaign sent — ${sent} delivered${failed ? `, ${failed} failed` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Campaign send failed');
    } finally {
      setCampaignSending(false);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Vision Sangam 2026</h1>
            <p className="text-sm text-gray-500">समाज चिंतन एवं विज़न संगम — registrations & approvals</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchRows(); fetchStats(); }} data-testid="vc-admin-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={openCampaign} data-testid="vc-admin-campaign-btn">
            <Mail className="w-4 h-4 mr-1" /> Send Campaign
          </Button>
          <Button size="sm" onClick={downloadExcel} data-testid="vc-admin-export">
            <Download className="w-4 h-4 mr-1" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="vc-admin-stats">
          <StatCard label="Total" value={stats.total} color="border-gray-200 bg-white" />
          <StatCard label="Pending" value={stats.pending} color="border-amber-200 bg-amber-50 text-amber-700" />
          <StatCard label="Approved" value={stats.approved} color="border-emerald-200 bg-emerald-50 text-emerald-700" />
          <StatCard label="Rejected" value={stats.rejected} color="border-rose-200 bg-rose-50 text-rose-700" />
          <StatCard label="Wants to Present" value={stats.wantsPresent} color="border-orange-200 bg-orange-50 text-orange-700" />
          <StatCard label="Collected ₹" value={stats.totalCollected} color="border-sky-200 bg-sky-50 text-sky-700" />
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-gray-500">Search</label>
            <div className="flex gap-2">
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                placeholder="Name, email, mobile, reg-code, place, nukh"
                data-testid="vc-admin-search"
              />
              <Button variant="outline" size="sm" onClick={doSearch}><Search className="w-4 h-4" /></Button>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500">Status</label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="h-9 w-36 px-3 rounded-md border border-input bg-background text-sm" data-testid="vc-admin-status-filter">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500">Wants to Present</label>
            <select value={wantsPresentFilter} onChange={(e) => { setWantsPresentFilter(e.target.value); setPage(0); }} className="h-9 w-36 px-3 rounded-md border border-input bg-background text-sm" data-testid="vc-admin-wants-filter">
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No registrations yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Nukh / Place</th>
                  <th className="px-4 py-3">Mobile / Email</th>
                  <th className="px-4 py-3">Present?</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-orange-700">{r.registrationCode}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-gray-500">{r.gender} · {r.age} yrs · s/o/w/o {r.fatherHusbandName}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{r.nukh}</div>
                      <div className="text-gray-500">{r.place}, {r.state}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{r.mobile}</div>
                      <div className="text-gray-500">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {r.wantsPresent
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-50 text-orange-700"><Sparkles className="w-3 h-3" /> {r.topic || 'Yes'}</span>
                        : <span className="text-gray-400">No</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-medium">₹{r.payment?.amount ?? 500}</div>
                      <div className="text-gray-500">{r.payment?.mode} · {r.payment?.utr}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-700'}`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setSelected(r)} title="View" data-testid={`vc-view-${r.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {r.status === 'approved' && (
                          <>
                            <Button size="icon" variant="ghost" onClick={() => downloadIdCard(r)} title="Download ID Card" className="text-sky-600" data-testid={`vc-download-idcard-${r.id}`}>
                              <IdCard className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => resendIdCardEmail(r)} title="Resend ID Card email" className="text-indigo-600" data-testid={`vc-resend-email-${r.id}`}>
                              <Mail className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {r.status !== 'approved' && (
                          <Button size="icon" variant="ghost" onClick={() => updateStatus(r, 'approved')} title="Approve (emails ID card)" className="text-emerald-600" data-testid={`vc-approve-${r.id}`}>
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        {r.status !== 'rejected' && (
                          <Button size="icon" variant="ghost" onClick={() => updateStatus(r, 'rejected')} title="Reject" className="text-rose-600" data-testid={`vc-reject-${r.id}`}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(r)} title="Delete" className="text-gray-500 hover:text-rose-600" data-testid={`vc-delete-${r.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t bg-gray-50">
            <PaginationBar
              currentPage={page + 1}
              totalPages={totalPages}
              onPageChange={(p) => setPage(p - 1)}
              totalItems={total}
              pageSize={LIMIT}
              itemLabel="registrations"
              testIdPrefix="vc-admin"
            />
          </div>
        )}
      </Card>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500 font-mono">{selected.registrationCode}</div>
                <div className="text-lg font-semibold">{selected.name}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelected(null)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  {selected.photoUrl && (
                    <img src={resolveFileUrl(selected.photoUrl)} alt={selected.name} className="w-full aspect-square object-cover rounded-lg border" />
                  )}
                </div>
                <div className="md:col-span-2 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs text-gray-500">Gender</div><div>{selected.gender}</div></div>
                  <div><div className="text-xs text-gray-500">Age</div><div>{selected.age}</div></div>
                  <div className="col-span-2"><div className="text-xs text-gray-500">Father / Husband</div><div>{selected.fatherHusbandName}</div></div>
                  <div><div className="text-xs text-gray-500">Nukh</div><div>{selected.nukh}</div></div>
                  <div><div className="text-xs text-gray-500">Place</div><div>{selected.place}, {selected.state}</div></div>
                  <div className="col-span-2"><div className="text-xs text-gray-500">Address</div><div>{selected.address}</div></div>
                  <div><div className="text-xs text-gray-500">Mobile</div><div>{selected.mobile}</div></div>
                  <div><div className="text-xs text-gray-500">Email</div><div className="break-all">{selected.email}</div></div>
                </div>
              </div>

              {selected.wantsPresent && (
                <div className="p-3 rounded-lg bg-orange-50 border border-orange-100">
                  <div className="text-xs text-orange-700 font-semibold mb-1">Wants to present ideas on</div>
                  <div className="text-sm">{selected.topic}</div>
                </div>
              )}

              {selected.remarks && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-100" data-testid="vc-selected-remarks">
                  <div className="text-xs text-amber-700 font-semibold mb-1">Remarks by Participant</div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">{selected.remarks}</div>
                </div>
              )}

              <div>
                <div className="text-sm font-semibold text-gray-800 mb-2">Payment</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs text-gray-500">Amount</div><div>₹{selected.payment?.amount}</div></div>
                  <div><div className="text-xs text-gray-500">Mode</div><div>{selected.payment?.mode}</div></div>
                  <div><div className="text-xs text-gray-500">UTR</div><div>{selected.payment?.utr}</div></div>
                  <div><div className="text-xs text-gray-500">Date</div><div>{formatDisplayDate(selected.payment?.date)}</div></div>
                </div>
                {selected.payment?.receiptUrl && (
                  <a href={resolveFileUrl(selected.payment.receiptUrl)} target="_blank" rel="noreferrer" className="inline-block mt-3 text-sm text-orange-600 hover:underline">
                    View payment receipt →
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">Delete registration?</h3>
            <p className="text-sm text-gray-600 mb-5">
              This will permanently remove <span className="font-medium">{confirmDelete.name}</span> ({confirmDelete.registrationCode}).
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button className="bg-rose-600 hover:bg-rose-700" onClick={deleteReg} data-testid="vc-confirm-delete">Delete</Button>
            </div>
          </div>
        </div>
      )}
      {/* Campaign Email Preview + Send Modal */}
      {campaignPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeCampaign}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="vc-campaign-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-amber-600" /> Send Vision Sangam Campaign
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  विषय: समाज और युवाओं के समक्ष चुनौतियों के समाधान की खोज
                </p>
              </div>
              {!campaignSending && (
                <button onClick={closeCampaign} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 mb-4">
              <div className="text-3xl font-bold text-amber-800">{campaignPreview.total}</div>
              <div className="text-xs text-amber-700 uppercase tracking-wide mt-0.5">unique recipients</div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm mb-5">
              <div className="rounded-md border border-gray-200 px-3 py-2 flex justify-between">
                <span className="text-gray-600">Families</span>
                <span className="font-medium text-gray-900">{campaignPreview.breakdown?.families ?? 0}</span>
              </div>
              <div className="rounded-md border border-gray-200 px-3 py-2 flex justify-between">
                <span className="text-gray-600">PS 2026 members</span>
                <span className="font-medium text-gray-900">{campaignPreview.breakdown?.ps ?? 0}</span>
              </div>
              <div className="rounded-md border border-gray-200 px-3 py-2 flex justify-between">
                <span className="text-gray-600">Professionals</span>
                <span className="font-medium text-gray-900">{campaignPreview.breakdown?.professionals ?? 0}</span>
              </div>
              <div className="rounded-md border border-gray-200 px-3 py-2 flex justify-between">
                <span className="text-gray-600">Donors</span>
                <span className="font-medium text-gray-900">{campaignPreview.breakdown?.donors ?? 0}</span>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              Emails are deduplicated across sources; counts above show contributions before dedup.
              Sent in batches of {campaignPreview.batchSize}.
            </p>

            {campaignSending && (
              <div className="mb-4" data-testid="vc-campaign-progress">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Batch {campaignProgress.current} / {campaignProgress.total}</span>
                  <span>{campaignProgress.sent} sent · {campaignProgress.failed} failed</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all"
                    style={{ width: `${campaignProgress.total ? (campaignProgress.current / campaignProgress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={closeCampaign} disabled={campaignSending}>
                {campaignSending ? 'Sending…' : 'Cancel'}
              </Button>
              <Button
                onClick={sendCampaign}
                disabled={campaignSending || !campaignPreview.total}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="vc-campaign-send"
              >
                {campaignSending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4 mr-1" /> Send Campaign</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisionConventionManagement;
